import * as React from "react";
import { Bell, Info, AlertTriangle, CalendarClock, CheckCircle } from "lucide-react";
import clsx from "clsx";

export interface Notification {
  id: string;
  type: "reminder" | "info" | "warning";
  title: string;
  message: string;
  timestamp: number;
  demo?: boolean;
}

interface NotificationManagerProps {
  notifications: Notification[];
  onDismiss: (id: string) => void;
  onClearAll?: () => void;
}

const iconMap = {
  reminder: <CalendarClock className="w-5 h-5 text-blue-500 shrink-0" />, // colloquio
  info: <Info className="w-5 h-5 text-sky-500 shrink-0" />, // info
  warning: <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0" />,
};

const NotificationManager: React.FC<NotificationManagerProps> = ({ notifications, onDismiss, onClearAll }) => {
  // Swipe to dismiss (mobile)
  const touchStartX = React.useRef<{ [id: string]: number }>({});
  
  // Auto-dismiss after 3 seconds
  React.useEffect(() => {
    const timers: { [id: string]: NodeJS.Timeout } = {};
    
    notifications.forEach((notification) => {
      if (!timers[notification.id]) {
        timers[notification.id] = setTimeout(() => {
          onDismiss(notification.id);
        }, 3000);
      }
    });
    
    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, [notifications, onDismiss]);
  
  const handleTouchStart = (id: string, e: React.TouchEvent) => {
    touchStartX.current[id] = e.touches[0].clientX;
  };
  const handleTouchEnd = (id: string, e: React.TouchEvent) => {
    const startX = touchStartX.current[id];
    const endX = e.changedTouches[0].clientX;
    if (startX !== undefined && Math.abs(endX - startX) > 60) {
      onDismiss(id);
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 px-2 w-full max-w-xs sm:max-w-sm md:max-w-md pointer-events-none">
      <div className="flex justify-end mb-1">
        <button
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded shadow pointer-events-auto"
          onClick={onClearAll}
        >
          <CheckCircle className="w-4 h-4" />
          Segna tutte come lette
        </button>
      </div>
      {notifications.map((n) => (
        <div
          key={n.id}
          className={clsx(
            "relative flex items-start gap-3 bg-white border-l-4 shadow-xl rounded-xl px-4 py-3 mb-1 pointer-events-auto animate-fade-in-up",
            n.type === "reminder" && "border-blue-500",
            n.type === "info" && "border-sky-500",
            n.type === "warning" && "border-yellow-500",
            "transition-all duration-300 ease-in-out",
            "sm:min-w-[320px] min-w-0 w-full",
            "mobile:py-4 mobile:px-3 mobile:text-base"
          )}
          style={{ boxShadow: "0 4px 24px 0 rgba(0,0,0,0.10)" }}
          onTouchStart={(e) => handleTouchStart(n.id, e)}
          onTouchEnd={(e) => handleTouchEnd(n.id, e)}
        >
          <div className="pt-1">{iconMap[n.type] || <Bell className="w-5 h-5 text-gray-400" />}</div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm flex items-center gap-2 mobile:text-base">
              {n.demo && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded mobile:text-sm">DEMO</span>}
              {n.title}
            </div>
            <div className="text-xs text-gray-700 mt-1 mobile:text-sm break-words leading-snug">
              {n.message}
            </div>
          </div>
          <button
            className="ml-2 text-gray-400 hover:text-gray-700 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-200 rounded transition-colors duration-150"
            onClick={() => onDismiss(n.id)}
            aria-label="Chiudi notifica"
            tabIndex={0}
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            ×
          </button>
        </div>
      ))}
      <style>{`
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.4s cubic-bezier(.4,0,.2,1);
        }
        @media (max-width: 640px) {
          .mobile\\:py-4 { padding-top: 1rem !important; padding-bottom: 1rem !important; }
          .mobile\\:px-3 { padding-left: 0.75rem !important; padding-right: 0.75rem !important; }
          .mobile\\:text-base { font-size: 1rem !important; }
          .mobile\\:text-sm { font-size: 0.875rem !important; }
        }
      `}</style>
    </div>
  );
};

export default NotificationManager; 